import { Link, Outlet, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMe } from '@/hooks/useMe';
import { auth, notifications } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { UserRole } from '@frt/shared-types';

interface NavItem {
  to: string;
  label: string;
  roles?: UserRole[]; // omit = visible to all authenticated
  group?: 'main' | 'admin';
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/customers', label: 'Customers' },
  { to: '/testers', label: 'Testers' },
  { to: '/profiles', label: 'Online Profiles' },
  { to: '/accounts', label: 'Accounts' },
  // Devices are edited inside a phone (mig 0020); device pages stay
  // reachable from a phone.
  { to: '/assets/phones', label: 'Phones' },
  { to: '/testers/compensation', label: 'Tester Compensation' },
  { to: '/tiller/upload', label: 'Tiller Upload', roles: ['admin', 'senior'] },
  { to: '/tiller/mapping', label: 'Tiller Mapping' },
  { to: '/tiller/unmapped', label: 'Unmapped Tiller', roles: ['admin', 'senior'] },
  { to: '/tiller/history', label: 'Upload History' },
  { to: '/manual-entry', label: 'Manual Entry' },
  { to: '/snapshots', label: 'Snapshots' },
  { to: '/reports/account-summary', label: 'Account Summary' },
  { to: '/reports/monthly-tracking', label: 'Monthly Tracking' },
  { to: '/reports/accounts-history', label: 'Accounts × Period' },
  { to: '/reports/transactions-export', label: 'Transactions Export' },
  { to: '/reports/linked-accounts', label: 'Linked Accounts Export' },
  { to: '/reports/builder', label: 'Custom Reports' },
  { to: '/reports/templates', label: 'Templates' },
  { to: '/admin/lookups', label: 'Lookups', roles: ['admin', 'senior'], group: 'admin' },
  { to: '/admin/users', label: 'Users', roles: ['admin'], group: 'admin' },
  { to: '/admin/audit', label: 'Audit Log', roles: ['admin'], group: 'admin' },
  { to: '/admin/config', label: 'Config', roles: ['admin'], group: 'admin' },
  { to: '/admin/imports', label: 'Bulk Import', roles: ['admin'], group: 'admin' },
];

export function RootLayout(): ReactNode {
  const router = useRouter();
  const path = router.state.location.pathname;
  const isPublic = path === '/login' || path === '/change-password';
  const { data: me, isLoading } = useMe();

  if (isPublic) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <Outlet />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (!me) {
    // Not logged in. Render outlet (the router beforeLoad already redirects)
    return <Outlet />;
  }

  return (
    <div className="min-h-full grid grid-cols-[16rem_1fr] grid-rows-[3.5rem_1fr]">
      <TopBar user={me.username} role={me.role} />
      <Sidebar role={me.role} currentPath={path} />
      <main className="overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}

function TopBar({ user, role }: { user: string; role: UserRole }) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const logout = useMutation({
    mutationFn: () => auth.logout(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await router.navigate({ to: '/login' });
    },
  });
  return (
    <header className="col-span-2 flex items-center justify-between border-b border-slate-200 bg-white px-4">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900">FRT Account Master</span>
        <span className="text-xs text-slate-500">Track 0 scaffold</span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <NotificationsBell />
        <Link to="/profile" className="text-slate-700">
          {user} <span className="text-xs text-slate-500">({role})</span>
        </Link>
        <button
          onClick={() => logout.mutate()}
          className="text-sm text-slate-500 hover:text-red-600"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

function Sidebar({ role, currentPath }: { role: UserRole; currentPath: string }) {
  const visible = NAV.filter((n) => !n.roles || n.roles.includes(role));
  const main = visible.filter((n) => n.group !== 'admin');
  const admin = visible.filter((n) => n.group === 'admin');
  return (
    <aside className="border-r border-slate-200 bg-white p-3 overflow-auto">
      <NavSection items={main} currentPath={currentPath} />
      {admin.length > 0 && (
        <>
          <div className="mt-6 mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Admin
          </div>
          <NavSection items={admin} currentPath={currentPath} />
        </>
      )}
    </aside>
  );
}

function NavSection({ items, currentPath }: { items: NavItem[]; currentPath: string }) {
  return (
    <ul className="space-y-1">
      {items.map((n) => (
        <li key={n.to}>
          <Link
            to={n.to}
            className={cn(
              'block rounded px-2 py-1.5 text-sm hover:bg-slate-100',
              currentPath === n.to ? 'bg-brand-50 text-brand-700 font-medium' : 'text-slate-700',
            )}
          >
            {n.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NotificationsBell() {
  // Tiny query — we just want unread_count for the badge. Refetch every 60s
  // so the badge stays fresh without aggressive polling.
  const { data } = useQuery({
    queryKey: ['notifications', 'badge'],
    queryFn: () => notifications.list({ per_page: 1 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unread = data?.unread_count ?? 0;
  return (
    <Link
      to="/notifications"
      className="relative inline-flex items-center text-slate-600 hover:text-slate-900"
      aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
    >
      <span>Notifications</span>
      {unread > 0 && (
        <span
          className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white"
          aria-hidden="true"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}
